import type { GridConfig } from "../components/GridConfigModal";
import type { SourceImage } from "./imageTypes";
import { getFileBaseName } from "./imageFormats";

export const stitchImages = async (
  imageSources: SourceImage[],
  config: GridConfig
): Promise<SourceImage | null> => {
  if (imageSources.length < 2) return null;

  try {
    const bitmaps = await Promise.all(
      imageSources.map((source) => createImageBitmap(source.blob))
    );

    const maxWidth = Math.max(...bitmaps.map((bitmap) => bitmap.width));
    const maxHeight = Math.max(...bitmaps.map((bitmap) => bitmap.height));
    const totalWidth = config.columns * maxWidth + (config.columns - 1) * config.gap;
    const totalHeight = config.rows * maxHeight + (config.rows - 1) * config.gap;

    const canvas = document.createElement("canvas");
    canvas.width = totalWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext("2d")!;

    if (config.backgroundColor !== "transparent") {
      ctx.fillStyle = config.backgroundColor;
      ctx.fillRect(0, 0, totalWidth, totalHeight);
    }

    bitmaps.forEach((bitmap, index) => {
      if (index >= config.columns * config.rows) return;

      const col = index % config.columns;
      const row = Math.floor(index / config.columns);
      const cellX = col * (maxWidth + config.gap);
      const cellY = row * (maxHeight + config.gap);

      let drawX: number;
      let drawY: number;
      let drawW: number;
      let drawH: number;
      const fit = config.fit || "true-size";

      if (fit === "true-size") {
        drawW = bitmap.width;
        drawH = bitmap.height;
        drawX = cellX + (maxWidth - drawW) / 2;
        drawY = cellY + (maxHeight - drawH) / 2;
      } else if (fit === "cover") {
        const scaleX = maxWidth / bitmap.width;
        const scaleY = maxHeight / bitmap.height;
        const fitScale = Math.max(scaleX, scaleY);
        drawW = bitmap.width * fitScale;
        drawH = bitmap.height * fitScale;
        drawX = cellX + (maxWidth - drawW) / 2;
        drawY = cellY + (maxHeight - drawH) / 2;
      } else {
        const scaleX = maxWidth / bitmap.width;
        const scaleY = maxHeight / bitmap.height;
        const fitScale = Math.min(scaleX, scaleY);
        drawW = bitmap.width * fitScale;
        drawH = bitmap.height * fitScale;
        drawX = cellX + (maxWidth - drawW) / 2;
        drawY = cellY + (maxHeight - drawH) / 2;
      }

      ctx.drawImage(bitmap, drawX, drawY, drawW, drawH);
      bitmap.close();
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error("Failed to create stitched image"));
      }, "image/png");
    });

    const url = URL.createObjectURL(blob);
    const baseName = imageSources[0]?.name ? getFileBaseName(imageSources[0].name) : "stitched";

    return {
      blob,
      url,
      name: `${baseName}-grid.png`,
      width: totalWidth,
      height: totalHeight,
    };
  } catch (err) {
    console.error("Failed to stitch images:", err);
    return null;
  }
};
